import styled, { keyframes, css } from 'styled-components';

/* ── paleta do design system (própria do componente, para ser portátil) ── */
const TEAL = 'var(--teal)';
const TEAL_TEXT = 'var(--teal-text, var(--teal))';
const TEAL_LIGHT = 'var(--teal-light)';
const TEAL_SOFT = 'var(--teal-soft)';
const GRAY_BG = 'var(--bg)';
const GRAY_LINE = 'var(--line)';
const GRAY_TEXT = 'var(--text-muted)';
const GREEN = 'var(--green)';
const RED = 'var(--red)';
const RED_SOFT = 'var(--red-soft)';

const bp = { desktop: '1024px', tablet: '768px', mobile: '540px', small: '420px' };

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

/* botão base compartilhado dentro do componente */
const btnBase = css`
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-weight: 700;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: transform 0.12s ease, filter 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  &:hover { filter: brightness(1.05); transform: translateY(-1px); }
  &:active { transform: translateY(0); }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; filter: none; }
`;

/* container full width — largura máxima maior, abaixo dos painéis */
export const AuditContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  box-sizing: border-box;
  padding: 22px 24px;
  border: 1px solid ${GRAY_LINE};
  border-radius: 16px;
  background: var(--surface);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: ${fadeUp} 0.35s ease backwards;
  @media (max-width: ${bp.mobile}) { padding: 18px 14px; border-radius: 14px; }
`;

export const AuditHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

export const AuditTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${TEAL_TEXT};
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const AuditFilters = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

export const FilterSelect = styled.select`
  box-sizing: border-box;
  padding: 9px 12px;
  border: 1px solid ${GRAY_LINE};
  border-radius: 10px;
  font-size: 13px;
  font-family: inherit;
  color: var(--text-2);
  background: ${GRAY_BG};
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:focus { outline: none; border-color: ${TEAL_LIGHT}; box-shadow: 0 0 0 3px rgba(10, 90, 115, 0.12); }
`;

export const DateField = styled.input`
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid ${GRAY_LINE};
  border-radius: 10px;
  font-size: 13px;
  font-family: inherit;
  color: var(--text-2);
  background: ${GRAY_BG};
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:focus { outline: none; border-color: ${TEAL_LIGHT}; box-shadow: 0 0 0 3px rgba(10, 90, 115, 0.12); }
  @media (max-width: ${bp.mobile}) { flex: 1; }
`;

export const DateSep = styled.span`
  font-size: 12.5px;
  color: ${GRAY_TEXT};
  font-weight: 600;
`;

export const ClearDates = styled.button`
  ${btnBase}
  background: #eef2f4;
  color: ${GRAY_TEXT};
  border: 1px solid ${GRAY_LINE};
  width: 30px;
  height: 30px;
  padding: 0;
  font-size: 13px;
  border-radius: 8px;
`;

export const AuditList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const AuditRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 12px 14px;
  border: 1px solid ${GRAY_LINE};
  border-radius: 11px;
  background: ${({ $revertido }) => ($revertido ? '#fafbfb' : 'var(--surface)')};
  opacity: ${({ $revertido }) => ($revertido ? 0.72 : 1)};
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
  &:hover { box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05); border-color: var(--teal-soft); }
  @media (max-width: ${bp.mobile}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

export const AuditInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
`;

export const AuditAction = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #223;
  span { font-weight: 500; color: ${GRAY_TEXT}; }
`;

export const AuditMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12.5px;
  color: ${GRAY_TEXT};
  flex-wrap: wrap;
`;

export const AuditBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${TEAL_SOFT};
  color: ${TEAL_TEXT};
  font-weight: 700;
  font-size: 12px;
`;

export const RevertButton = styled.button`
  ${btnBase}
  background: ${RED_SOFT};
  color: ${RED};
  border: 1px solid #f1cdcd;
  padding: 8px 16px;
  font-size: 13px;
  flex-shrink: 0;
  @media (max-width: ${bp.mobile}) { width: 100%; }
`;

export const RevertedTag = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  color: ${GRAY_TEXT};
  background: #eef2f4;
  border: 1px solid ${GRAY_LINE};
  padding: 5px 11px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

export const AuditEmpty = styled.div`
  text-align: center;
  color: ${GRAY_TEXT};
  font-size: 14px;
  padding: 28px 12px;
`;

export const AuditLoading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px 12px;
  color: ${GRAY_TEXT};
  font-size: 13.5px;
  font-weight: 600;
  animation: ${fadeUp} 0.2s ease;
`;

export const Spinner = styled.div`
  width: 34px;
  height: 34px;
  border: 3px solid ${GRAY_LINE};
  border-top-color: ${TEAL};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

export const AuditFeedback = styled.div`
  border-radius: 10px;
  padding: 11px 14px;
  font-size: 13.5px;
  font-weight: 600;
  line-height: 1.45;
  animation: ${fadeUp} 0.2s ease;
  ${({ $tipo }) =>
    $tipo === 'ok'
      ? css`background: #e7f5ee; color: ${GREEN}; border: 1px solid #bfe3ce;`
      : css`background: ${RED_SOFT}; color: ${RED}; border: 1px solid #f1cdcd;`}
`;

export const LoadMoreButton = styled.button`
  ${btnBase}
  align-self: center;
  background: ${GRAY_BG};
  color: ${TEAL_TEXT};
  border: 1px solid ${GRAY_LINE};
  padding: 10px 22px;
  font-size: 13.5px;
`;

/* ── Modal de confirmação de reversão ── */
const fadeInModal = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const popIn = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

export const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 30, 38, 0.5);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
  animation: ${fadeInModal} 0.15s ease;
  @media (max-width: 540px) { padding: 0; align-items: flex-end; }
`;

export const ConfirmCard = styled.div`
  background: var(--surface);
  border-radius: 16px;
  padding: 24px 22px;
  width: 100%;
  max-width: 460px;
  box-sizing: border-box;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.25);
  animation: ${popIn} 0.2s ease;
  max-height: 88vh;
  overflow-y: auto;
  @media (max-width: 540px) { border-radius: 18px 18px 0 0; max-width: 100%; }
`;

export const ConfirmTitle = styled.h2`
  margin: 0 0 16px;
  font-size: 18px;
  font-weight: 800;
  color: var(--amber, #d97706);
  text-align: center;
`;

export const ConfirmBlock = styled.div`
  margin-bottom: 12px;
  padding: 10px 12px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 10px;
`;

export const ConfirmLabel = styled.div`
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #5b6b72;
  margin-bottom: 3px;
`;

export const ConfirmValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #223;
  line-height: 1.4;
`;

export const ConfirmWarn = styled.div`
  margin: 14px 0 20px;
  padding: 12px 14px;
  background: #fdf4e6;
  border: 1px solid #f0d9ac;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.5;
  color: #7a5410;
  strong { color: #5f400a; }
`;

export const ConfirmActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  @media (max-width: 420px) {
    flex-direction: column-reverse;
    button { width: 100%; }
  }
`;

export const ConfirmCancel = styled.button`
  ${btnBase}
  background: var(--surface-2);
  color: #5b6b72;
  border: 1px solid var(--line);
  padding: 11px 20px;
  font-size: 14px;
`;

export const ConfirmOk = styled.button`
  ${btnBase}
  background: #cf3a3a;
  color: #fff;
  padding: 11px 22px;
  font-size: 14px;
`;